const _ = require("lodash");
const eases = require("eases");
const inBrowser = global.document && global.window;

// When scrolling to an element, allow for this many pixels on the top.
const SCROLL_NUDGE = 10;

const getScrollPos = function() {
	return (
		window.pageYOffset ||
		document.documentElement.scrollTop ||
		document.body.scrollTop
	);
};

const getFullHeight = function() {
	return Math.max(
		document.body.scrollHeight,
		document.body.offsetHeight,
		document.documentElement.clientHeight,
		document.documentElement.scrollHeight,
		document.documentElement.offsetHeight
	);
};

const scrollToPos = function(px) {
	document.documentElement.scrollTop = document.body.scrollTop = px;
};

// Returns true if el is an HTML element currently in the DOM.
const isDisplayedEl = function(el) {
	return el && el.getBoundingClientRect && el.getBoundingClientRect().width > 0;
};

const getThingPos = function(thing, options = {}) {
	// Number check done first so that passing 0 means scroll to top.
	if (typeof thing === "number") {
		return thing;
	} else if (!thing || thing === "current") {
		return null;
	} else if (thing === "top") {
		return 0;
	}
	// The only other value to process is an HTML element. Before we proceed,
	// verify it's on the page.
	if (thing && Array.isArray(thing)) {
		thing = _.find(thing, isDisplayedEl);
	}
	if (!isDisplayedEl(thing)) {
		return null;
	}
	const rect = thing.getBoundingClientRect();
	const globalScrollTop = getScrollPos();
	const nudge = options.noNudge ? 0 : SCROLL_NUDGE;
	const finalPos = Math.max(rect.top + globalScrollTop - nudge, 0);
	return finalPos;
};

// Determines whether scroll position is within the nudge of the bottom.
const isAtBottom = function() {
	return getScrollPos() + window.innerHeight > getFullHeight() - SCROLL_NUDGE;
};

// thing can be one of a few different values:
// - an integer, giving the scroll position to go to.
// - "current", meaning don't scroll.
// - "top", meaning scroll to top. Same as passing 0.
// - an HTML element, meaning to scroll to it with a bit of padding.
// - an array of HTML elements, meaning to scroll to first one on the page.
const scrollTo = function(thing) {
	if (!inBrowser) {
		return;
	}
	const pos = getThingPos(thing);
	if (_.isNumber(pos)) {
		scrollToPos(pos);
	}
};

const currRAFTable = {};
// Options for animation:
// - id: optional string name of the animation. If two animations with the same
//   ID are started concurrently, the first one is cancelled.
// - scrollThing: an element or position to scroll to (defaults to "top").
// - time: the total time the animation should take, in ms (defaults to 500).
// - timeRatio: the time in ms per pixel traversed that the animation should
//   take, overrides time.
// - maxTime: the max time in ms that the animation should take when
//   calculating time using timeRatio.
// - offset: scroll to scrollThing with offset. Defaults to 0.
const animateScroll = function(options = {}) {
	if (!inBrowser) {
		return;
	}
	const {
		id = "",
		scrollThing = "top",
		time = 500,
		timeRatio,
		maxTime,
		offset = 0,
	} = options;
	// If an animation is already going, cancel it.
	if (id && currRAFTable[id]) {
		cancelAnimateScroll(id);
	}

	let startTime = null;
	const startPos = getScrollPos();
	const endPos = getThingPos(scrollThing, {noNudge: true}) + offset;
	if (startPos === endPos) {
		return;
	}
	let animTime = time || 500;
	if (timeRatio) {
		// TODO: Not sure if this needs adjustments for higher DPR screens.
		animTime = timeRatio * Math.abs(endPos - startPos);
		if (maxTime) {
			animTime = Math.min(animTime, maxTime);
		}
	}

	// If no ID is passed, the below code overwrites currRAFTable[""] which might
	// already exist. Since cancelAnimateScroll can't interfere with that value
	// due to the falsy id, this has no harm.
	const animateStep = t => {
		if (!startTime) {
			startTime = t;
		}
		const percent = eases.sineInOut(Math.min(1, (t - startTime) / animTime));
		scrollToPos(startPos + percent * (endPos - startPos));
		if (percent < 1) {
			currRAFTable[id] = window.requestAnimationFrame(animateStep);
		} else {
			delete currRAFTable[id];
		}
	};
	currRAFTable[id] = window.requestAnimationFrame(animateStep);
};

// Pass an ID previously sent to animateScroll.
const cancelAnimateScroll = function(id) {
	if (inBrowser && id && currRAFTable[id]) {
		window.cancelAnimationFrame(currRAFTable[id]);
		delete currRAFTable[id];
	}
};

// Given an element in the DOM, if not enough of it is scrolled to, animate
// scrolling to it. Pass an object with:
// - id: optional string name of the animation. See animateScroll.
// - el: required. The DOM element to scroll to if needed.
// - scrollDir: optional, either "down" (default) or "up". The direction that
//   we would scroll if a scroll is necessary.
// - smallCutoff: Optional. The cutoff determining whether to scroll on small
//   screens, meaning less than 700px width. Given as a fraction of the el
//   height. If omitted, never scroll for this screen size.
// - largeCutoff: Like smallCutoff, but for screens larger than 700px in width.
const ensureElVisible = function(props) {
	const {id, el, fromDir, smallCutoff, largeCutoff} = props;
	if (inBrowser && el && el.getBoundingClientRect) {
		const smallScreenCutoff = 700;
		const rect = el.getBoundingClientRect();
		// If scroll would go down, this measures how much of the view is visible
		// on the screen. Otherwise, it measures how much of the view is offscreen.
		const viewMeasure =
			fromDir !== "up" ? window.innerHeight - rect.top : rect.top;
		const largeCutoffApplies =
			window.innerWidth > smallScreenCutoff &&
			largeCutoff &&
			viewMeasure < rect.height * largeCutoff;
		const smallCutoffApplies =
			window.innerWidth <= smallScreenCutoff &&
			smallCutoff &&
			viewMeasure < rect.height * smallCutoff;
		if (largeCutoffApplies || smallCutoffApplies) {
			animateScroll({
				id,
				scrollThing: el,
				timeRatio: 0.65,
				maxTime: 1000,
			});
		}
	}
};

module.exports = {
	isAtBottom,
	scrollTo,
	animateScroll,
	cancelAnimateScroll,
	ensureElVisible,
};
